import { FC, useEffect } from 'react';
import { Box, Flex, Text, theme } from '@chakra-ui/react';
import { NavigationLink, NavigationLogo, NavigationProfile } from '../../types/Navigation';
import { CbNavigationLogo } from './CbNavigationLogo';
import { CbNavigationLink } from './CbNavigationLink';
import { CbNavigationLinkPopover } from './CbNavigationLinkPopover';
import { CbNavigationProfilePopover } from './CbNavigationProfilePopover';
import { CbNavigationDrawer } from './CbNavigationDrawer';
import { CbRaffleTicketBalance } from '../CbRaffleTicketBalance';
import { CbChestBalance } from '../CbChestBalance';
import { CbTokenBalance } from '../CbTokenBalance';
import { CbSpectatorViewPopover } from './CbSpectatorViewPopover';
import { CbNotificationCenter } from '../CbNotificationCenter';
import { BetaTag } from '../BetaTag';
import { SupportButton } from './SupportButton';
import { MaintenanceWarningIcon } from './MaintenanceWarningIcon';
export interface CbNavigationBarProps {
  assumedUser?: {};
  chestBalance?: number;
  chestBalancePending?: boolean;
  companyName?: string;
  fullWidth?: boolean;
  hasChestBalance?: boolean;
  hasSpectatorView?: boolean;
  links: NavigationLink[];
  mobileLinks?: NavigationLink[];
  logo?: NavigationLogo;
  onAssumableUserChange?: (selectedOption: {}) => void;
  onChestBalanceClick?: () => Promise<void>;
  onResetClick?: () => void;
  onTokenBalanceClick?: () => Promise<void>;
  profile?: NavigationProfile;
  raffleTicketBalance?: number;
  raffleTicketBalancePending?: boolean;
  showMaintenanceWarning?: boolean;
  isMobile?: boolean;
  tokenBalance?: number;
  tokenBalancePending?: boolean;
}
export const CbNavigationBar: FC<CbNavigationBarProps> = ({
  assumedUser = {},
  chestBalance = 0,
  chestBalancePending = true,
  companyName = null,
  fullWidth = false,
  hasChestBalance,
  hasSpectatorView,
  isMobile,
  links,
  mobileLinks,
  logo,
  onAssumableUserChange,
  onChestBalanceClick,
  onResetClick,
  onTokenBalanceClick,
  profile,
  raffleTicketBalance = 0,
  raffleTicketBalancePending = true,
  showMaintenanceWarning = false,
  tokenBalance = 0,
  tokenBalancePending = true
}) => {
  useEffect(() => {
    // This is a workaround to force update chakra Popover/popper.js positioning if links change
    if (typeof window !== typeof undefined) {
      window.dispatchEvent(new Event('resize'));
    }
  }, [links]);
  const userLinks = !!links ? links.filter(link => !link.isManager) : [];
  const managerLinks = !!links ? links.filter(link => link.isManager) : [];
  const raffleTicketBalancePart = <Box as={onChestBalanceClick ? 'button' : 'div'} backgroundColor="luigisPants.400" borderRadius="100px" marginRight={2} paddingLeft={2} paddingRight="10px" paddingY={1} onClick={onChestBalanceClick}>
      <CbRaffleTicketBalance raffleTicketBalance={raffleTicketBalance} color="white" isPending={raffleTicketBalancePending} size="sm" />
    </Box>;
  const chestBalancePart = <Box as={onChestBalanceClick ? 'button' : 'div'} backgroundColor="luigisPants.400" borderRadius="100px" marginRight={2} paddingLeft={2} paddingRight="10px" paddingY={1} onClick={onChestBalanceClick}>
      <CbChestBalance chestBalance={chestBalance} color="white" isPending={chestBalancePending} size="sm" />
    </Box>;
  const tokenBalancePart = <Box as={onTokenBalanceClick ? 'button' : 'div'} backgroundColor="luigisPants.400" borderRadius="100px" paddingX={2} paddingY={1} onClick={onTokenBalanceClick}>
      <CbTokenBalance color="white" isPending={tokenBalancePending} size="sm" tokenBalance={tokenBalance} />
    </Box>;
  const NavigationDivider = () => <Box color="white" data-sentry-element="Box" data-sentry-component="NavigationDivider" data-sentry-source-file="CbNavigationBar.tsx">|</Box>;
  const renderLink = (link: NavigationLink) => {
    if (link.subLinks?.length) {
      return <CbNavigationLinkPopover key={link.label} isActive={link.isActive} icon={link.icon} label={link.label} subLinks={link.subLinks} />;
    }
    return <CbNavigationLink key={link.label} as={link.as} href={link.href} isActive={link.isActive} isExternal={link.isExternal} icon={link.icon} renderAsButton={link.renderAsButton} data-sentry-element="CbNavigationLink" data-sentry-component="renderLink" data-sentry-source-file="CbNavigationBar.tsx">
        {link.label}
        {link.isBeta && <BetaTag />}
      </CbNavigationLink>;
  };
  return <Flex alignItems={'center'} as="nav" backgroundColor="luigisPants.500" paddingX={4} data-sentry-element="Flex" data-sentry-component="CbNavigationBar" data-sentry-source-file="CbNavigationBar.tsx">
      <Flex alignItems="center" marginX={{
      md: fullWidth ? 0 : 'auto'
    }} width={{
      base: '100%',
      md: fullWidth ? '100%' : '100%'
    }} data-sentry-element="Flex" data-sentry-source-file="CbNavigationBar.tsx">
        <Flex alignItems="center" justifyContent={isMobile ? 'space-between' : 'flex-start'} width="100%" data-sentry-element="Flex" data-sentry-source-file="CbNavigationBar.tsx">
          {/* Set zIndex as adjacent Chakra popover content can blanket link */}
          <Flex alignItems="center" zIndex={theme.zIndices.dropdown} data-sentry-element="Flex" data-sentry-source-file="CbNavigationBar.tsx">
            {!!logo && <>
                <CbNavigationLogo as={logo.as} color="white" href={logo.href} element={isMobile && logo.mobileElement ? logo.mobileElement : logo.element} marginRight={isMobile ? 4 : '22px'} onClick={logo.onClick} />
                {!isMobile && <Box backgroundColor="text.tertiaryWhite" height="37px" width="1px" />}
              </>}
            {isMobile && <>
                {raffleTicketBalance ? raffleTicketBalancePart : null}
                {hasChestBalance && chestBalancePart}
                {tokenBalancePart}
                {hasSpectatorView && <Box marginLeft={2}>
                    <CbSpectatorViewPopover initValue={assumedUser} isMobile={isMobile} onAssumableUserChange={onAssumableUserChange} onResetClick={onResetClick} />
                  </Box>}
                <Box marginLeft={2}>
                  <CbNotificationCenter />
                </Box>
                {showMaintenanceWarning && <Box marginLeft={2}>
                    <MaintenanceWarningIcon />
                  </Box>}
              </>}
          </Flex>
          {isMobile && mobileLinks ? <Flex alignItems={'center'}>
              <SupportButton isMobile />
              <CbNavigationDrawer links={mobileLinks} profile={profile} />
            </Flex> : <>
              {/* Set zIndex as adjacent Chakra popover content can blanket link  */}
              <Flex alignItems={'center'} zIndex={theme.zIndices.dropdown}>
                {userLinks.map(renderLink)}
                {managerLinks.length > 0 && <NavigationDivider />}
                {managerLinks.map(renderLink)}
              </Flex>
              <Flex marginLeft="auto" alignItems="center">
                {!!companyName && <Text color="cloud.500" marginRight={2}>
                    {companyName}
                  </Text>}
                {showMaintenanceWarning && <Box marginRight={1}>
                    <MaintenanceWarningIcon />
                  </Box>}
                {hasSpectatorView && <Box marginRight={2}>
                    <CbSpectatorViewPopover initValue={assumedUser} onAssumableUserChange={onAssumableUserChange} onResetClick={onResetClick} />
                  </Box>}
                {raffleTicketBalance ? raffleTicketBalancePart : null}
                {hasChestBalance && chestBalancePart}
                {tokenBalancePart}
                <Box marginLeft={2}>
                  <CbNotificationCenter />
                </Box>
                <SupportButton />
                {!!profile && <Box marginLeft={2}>
                    <CbNavigationProfilePopover hasProfileImage={profile.hasProfileImage} imageUrl={profile.imageUrl} isActive={profile.isActive} icon={profile.icon} label={profile.label} name={profile.name} subLinks={profile.subLinks} />
                  </Box>}
              </Flex>
            </>}
        </Flex>
      </Flex>
    </Flex>;
};